/**
 * Unified Razorpay Payment Service
 * Shared across Stay, Meeting Room, and future booking products.
 */
import { getApiUrl } from '../config/api';

// ─── Razorpay Script Loader ──────────────────────────────────────────────────

let razorpayScriptPromise = null;

export const loadRazorpayScript = () => {
  if (window.Razorpay) return Promise.resolve(true);
  if (razorpayScriptPromise) return razorpayScriptPromise;

  razorpayScriptPromise = new Promise((resolve) => {
    const existing = document.querySelector('script[src*="checkout.razorpay.com"]');
    if (existing) {
      existing.addEventListener('load', () => resolve(true));
      existing.addEventListener('error', () => resolve(false));
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.head.appendChild(script);
  });

  return razorpayScriptPromise;
};

// ─── Order Creation ──────────────────────────────────────────────────────────

/**
 * Create a Razorpay order for a Stay booking.
 */
export const createStayPaymentOrder = async (token, payload) => {
  const res = await fetch(getApiUrl('/api/payments/create-order'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(payload),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.message || 'Failed to create stay payment order');
  return data;
};

/**
 * Create a Razorpay order for a Meeting Room booking.
 * @param {string} token - JWT auth token
 * @param {string} bookingId - existing meeting booking ID
 * @param {'full'|'partial'} paymentType - full or 50% partial payment
 */
export const createMeetingPaymentOrder = async (token, bookingId, paymentType = 'full') => {
  const res = await fetch(getApiUrl('/api/payments/meeting/create-order'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ bookingId, paymentType }),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.message || 'Failed to create meeting payment order');
  return data;
};

// ─── Checkout ────────────────────────────────────────────────────────────────

/**
 * Open Razorpay Checkout modal.
 * Returns resolved payment response on success; rejects on cancel/failure.
 *
 * @param {object} orderData - { orderId, key, amount, currency }
 * @param {object} meta      - { name, description, prefill: { name, email, contact } }
 */
export const openRazorpayCheckout = (orderData, meta = {}) => {
  return new Promise((resolve, reject) => {
    if (!window.Razorpay) {
      reject(new Error('Payment gateway not loaded. Please refresh the page and try again.'));
      return;
    }

    const options = {
      key: orderData.key,
      amount: orderData.amount,
      currency: orderData.currency || 'INR',
      order_id: orderData.orderId,
      name: meta.name || 'UBSAAPP',
      description: meta.description || 'Booking Payment',
      image: meta.logo || '',
      prefill: {
        name: meta.prefill?.name || '',
        email: meta.prefill?.email || '',
        contact: meta.prefill?.contact || '',
      },
      theme: { color: '#2563eb' },
      handler: (response) => {
        resolve({
          razorpay_order_id: response.razorpay_order_id,
          razorpay_payment_id: response.razorpay_payment_id,
          razorpay_signature: response.razorpay_signature,
        });
      },
      modal: {
        ondismiss: () => reject(new Error('Payment cancelled by user')),
      },
    };

    const rzp = new window.Razorpay(options);
    rzp.on('payment.failed', (resp) => {
      reject(new Error(resp.error?.description || 'Payment failed'));
    });
    rzp.open();
  });
};

// ─── Verification ────────────────────────────────────────────────────────────

/**
 * Verify Razorpay payment signature on backend.
 */
export const verifyPayment = async (token, paymentResponse) => {
  const res = await fetch(getApiUrl('/api/payments/verify'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(paymentResponse),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.message || 'Payment verification failed');
  return data;
};

// ─── Full Payment Flow (convenience wrapper) ─────────────────────────────────

/**
 * Complete end-to-end Razorpay payment flow.
 *
 * @param {object} opts
 * @param {'stay'|'meeting'} opts.bookingType
 * @param {string} opts.token  - JWT
 * @param {object} opts.orderPayload - payload for createOrder API
 * @param {object} opts.checkoutMeta - { name, description, prefill }
 * @returns {object} { paymentResult, verifyResult }
 */
export const processPayment = async ({ bookingType, token, orderPayload, checkoutMeta }) => {
  // Ensure Razorpay script is loaded
  const loaded = await loadRazorpayScript();
  if (!loaded) throw new Error('Failed to load Razorpay payment gateway.');

  // Create Razorpay order
  let orderData;
  if (bookingType === 'meeting') {
    orderData = await createMeetingPaymentOrder(token, orderPayload.bookingId, orderPayload.paymentType);
  } else {
    orderData = await createStayPaymentOrder(token, orderPayload);
  }

  // Open checkout
  const paymentResponse = await openRazorpayCheckout(orderData, checkoutMeta);

  // Verify on backend
  const verifyResult = await verifyPayment(token, paymentResponse);

  return { orderData, paymentResponse, verifyResult };
};

// ─── User Payment History ────────────────────────────────────────────────────

export const fetchMyPayments = async (token, type) => {
  const url = type ? getApiUrl(`/api/payments/my-payments?type=${type}`) : getApiUrl('/api/payments/my-payments');
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  const data = await res.json();
  if (!data.success) throw new Error(data.message || 'Failed to fetch payments');
  return data.payments;
};

export const fetchPaymentInvoice = async (token, paymentId) => {
  const res = await fetch(getApiUrl(`/api/payments/${paymentId}/invoice`), {
    headers: { Authorization: `Bearer ${token}` },
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.message || 'Failed to fetch invoice');
  return data.invoice;
};

// ─── Payment Status Helpers ──────────────────────────────────────────────────

export const PAYMENT_STATUS = {
  paid: { label: 'Paid', color: '#10b981', bg: '#d1fae5', textColor: '#065f46' },
  pending: { label: 'Pending', color: '#f59e0b', bg: '#fef3c7', textColor: '#92400e' },
  failed: { label: 'Failed', color: '#ef4444', bg: '#fee2e2', textColor: '#991b1b' },
  refunded: { label: 'Refunded', color: '#3b82f6', bg: '#dbeafe', textColor: '#1e40af' },
};

export const getPaymentStatusStyle = (status) =>
  PAYMENT_STATUS[status] || { label: status, color: '#6b7280', bg: '#f3f4f6', textColor: '#374151' };

// ─── Invoice PDF Generator ───────────────────────────────────────────────────

export const generateInvoicePDF = async (invoice) => {
  const { jsPDF } = await import('jspdf');
  const doc = new jsPDF();

  const primary = [37, 99, 235];
  const gray = [107, 114, 128];
  const black = [17, 24, 39];
  const lightGray = [249, 250, 251];

  // Header background
  doc.setFillColor(...primary);
  doc.rect(0, 0, 210, 40, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.text('UBSAAPP', 15, 18);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text('Unified Booking & Stay App', 15, 26);
  doc.text('support@ubsaapp.com', 15, 33);

  // Invoice title
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text('INVOICE', 150, 18);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(`#${String(invoice._id).slice(-8).toUpperCase()}`, 150, 26);

  const paidDate = invoice.paidAt ? new Date(invoice.paidAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
  doc.text(`Date: ${paidDate}`, 150, 33);

  // Reset text color
  doc.setTextColor(...black);

  // Bill To section
  let y = 55;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text('BILL TO', 15, y);
  y += 6;
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...gray);
  doc.text(invoice.user?.name || '—', 15, y); y += 5;
  doc.text(invoice.user?.email || '—', 15, y); y += 5;
  if (invoice.user?.phoneNumber) { doc.text(invoice.user.phoneNumber, 15, y); y += 5; }

  // Booking details box
  doc.setTextColor(...black);
  y += 5;
  doc.setFillColor(...lightGray);
  doc.roundedRect(15, y, 180, 50, 2, 2, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  y += 10;
  doc.text('Booking Details', 20, y);
  y += 8;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);

  const bookingType = invoice.bookingType || 'stay';
  doc.setTextColor(...gray);

  if (bookingType === 'stay') {
    doc.text(`Property: ${invoice.property?.name || '—'}`, 20, y); y += 6;
    const checkIn = invoice.booking?.checkIn ? new Date(invoice.booking.checkIn).toLocaleDateString('en-IN') : '—';
    const checkOut = invoice.booking?.checkOut ? new Date(invoice.booking.checkOut).toLocaleDateString('en-IN') : '—';
    doc.text(`Check-in: ${checkIn}`, 20, y);
    doc.text(`Check-out: ${checkOut}`, 100, y); y += 6;
    doc.text(`Location: ${invoice.property?.city || ''}, ${invoice.property?.state || ''}`, 20, y);
  } else {
    doc.text(`Meeting Room: ${invoice.room?.name || '—'}`, 20, y); y += 6;
    const start = invoice.booking?.startTime ? new Date(invoice.booking.startTime).toLocaleString('en-IN') : '—';
    const end = invoice.booking?.endTime ? new Date(invoice.booking.endTime).toLocaleTimeString('en-IN') : '—';
    doc.text(`Start: ${start}`, 20, y);
    doc.text(`End: ${end}`, 100, y); y += 6;
    doc.text(`Purpose: ${invoice.booking?.purpose || '—'}`, 20, y);
  }

  // Amount section
  y += 20;
  doc.setTextColor(...black);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text('Payment Summary', 15, y);

  y += 8;
  doc.setLineWidth(0.3);
  doc.setDrawColor(...gray);
  doc.line(15, y, 195, y);
  y += 6;

  const rows = [
    ['Total Amount', `₹${(invoice.totalAmount || 0).toLocaleString('en-IN')}`],
    ['Platform Commission', `₹${(invoice.commissionAmount || 0).toLocaleString('en-IN')}`],
    ['Payment ID', invoice.razorpayPaymentId || '—'],
    ['Status', 'PAID'],
  ];

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  rows.forEach(([label, value]) => {
    doc.setTextColor(...gray);
    doc.text(label, 20, y);
    doc.setTextColor(...black);
    doc.text(value, 140, y, { align: 'right' });
    y += 7;
  });

  // Total box
  y += 4;
  doc.setFillColor(...primary);
  doc.roundedRect(130, y, 65, 14, 2, 2, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text(`₹${(invoice.totalAmount || 0).toLocaleString('en-IN')}`, 162, y + 9, { align: 'center' });

  // Footer
  doc.setTextColor(...gray);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text('Thank you for using UBSAAPP. This is a computer-generated invoice.', 105, 280, { align: 'center' });

  const filename = `ubsaapp-invoice-${String(invoice._id).slice(-8)}.pdf`;
  doc.save(filename);
};
